import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import Api from "@/js/api/authorisedApi";
import closeIcon from "@/assets/IconAnnouncementExit.svg?url";
import { envConfig } from "@/config";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import useStore from "@/stores";

interface Announcement {
	// System Incident
	id: string;
	title: string;
	name: string;
	type: string;
}

interface StatusIoResponse {
	announcements: Announcement[];
}

// Classwize navigation is sketchy... The AnnouncementBar is re-initialized on
// every navigation hence we use ES module level object to retain user selection.
// This also results in excessive number of fetch calls but addressing that is
// beyond the scope of CF-557...
const hiddenAnnouncements: Record<string, boolean> = {};

export const AnnouncementBar: FC = () => {
	const [announcements, setAnnouncements] = useState<Announcement[]>([]);

	const [deviceId, region, timezone] = useUserSessionStore(
		(state) => [state.deviceinfo?.deviceid, state.deviceinfo?.region ?? "", state.deviceinfo?.timezone ?? ""] as const
	);

	const showAnnouncements = useStore(
		(state) => state.featureToggles.lastFetched > 0 && !state.featureToggles.data["hide-status-page-banner"]
	);

	const pollingIntervalRef = useRef<ReturnType<typeof setInterval>>();

	useEffect(() => {
		if (deviceId && showAnnouncements) {
			fetchAnnouncements();
			pollingIntervalRef.current = setInterval(fetchAnnouncements, envConfig.STATUS_PAGE_FETCH_INTERVAL);
		} else {
			cancelAnnouncementsFetch();
		}

		return cancelAnnouncementsFetch;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [deviceId, showAnnouncements]);

	const fetchAnnouncements = () => {
		Api.get("/incidents", statusChange, (err: unknown) => {
			console.log(err);
		});
	};

	const cancelAnnouncementsFetch = () => {
		if (pollingIntervalRef.current) {
			clearInterval(pollingIntervalRef.current);
			pollingIntervalRef.current = undefined;
		}
		setAnnouncements([]);
	};

	// api-dispatcher always concatenates the region to the title of the announcement, as defined in statusio_handler.py
	const statusIoRegion = useMemo(() => {
		switch (region) {
			case "us-1": {
				// New Zealand features two different timezones
				const nzTimezones = ["Pacific/Auckland", "Pacific/Chatham"];
				if (nzTimezones.includes(timezone)) {
					return "New Zealand";
				}
				// Non New Zealand timezones are considered USA
				return "USA";
			}
			case "syd-2": {
				return "Australia";
			}
			case "uk-1": {
				return "United Kingdom";
			}
			// If the region is a development region, show all announcements
			default: {
				return "";
			}
		}
	}, [region, timezone]);

	const statusChange = (statusio: StatusIoResponse) => {
		const shouldDisplay = (announcement: Announcement) => {
			if (hiddenAnnouncements[announcement.id]) {
				return false;
			}

			// If the region is a production region, only show announcements that are for the current region
			const productionRegions = ["us-1", "syd-2", "uk-1"];
			if (productionRegions.includes(region)) {
				return announcement.title.includes(statusIoRegion);
			}

			// If the region is a development region, show all announcements
			return true;
		};

		setAnnouncements(statusio.announcements?.filter(shouldDisplay) ?? []);
	};

	const closeAnnouncement = (announcementId: string) => {
		hiddenAnnouncements[announcementId] = true;
		setAnnouncements((existing) => existing.filter((a) => a.id !== announcementId));
	};

	const handleLink = () => {
		window.open("http://status.linewize.net/", "_blank");
	};

	return (
		<div className="announcement-bar-container" data-testid="announcement-bar-container">
			{announcements.map((announcement) => {
				return (
					<div className="status-bar-open" key={announcement.id} data-testid="announcement-bar-item">
						<div className="announcement-type">{announcement.type}</div>
						<div className="announcement-spacer">-</div>
						<div className="announcement-title">{announcement.title}</div>
						<div className="announcement-name">{announcement.name}</div>
						<button type="button" className="announcement-link" onClick={handleLink}>
							VIEW DETAILS
						</button>
						<button onClick={() => closeAnnouncement(announcement.id)} className="ab-close-button" aria-label="Dismiss Announcement">
							<img src={closeIcon} alt="Close announcement" aria-hidden="true" />
						</button>
					</div>
				);
			})}
		</div>
	);
};
